import { Fragment, useId, useState } from "react";

import { ChevronDownIcon } from "@jti/brand-icons/chevron-down";
import { ChevronUpIcon } from "@jti/brand-icons/chevron-up";
import { Icon } from "@jti/core-components";
import { Separator } from "@jti/core-components/components/Separator";
import { useTranslations } from "@jti/ui";

import type { ExpandedBundleItemProps } from "./components/ExpandedBundleItem/ExpandedBundleItem";
import { ExpandedBundleItem } from "./components/ExpandedBundleItem/ExpandedBundleItem";
import type { ProductLineItemProps } from "./components/ProductLineItem/ProductLineItem";
import { ProductLineItem } from "./components/ProductLineItem/ProductLineItem";

import styles from "./ItemsSummaryView.module.scss";

export type SummaryItem =
  | { type: "product"; item: ProductLineItemProps; id: string | number }
  | { type: "bundle"; item: ExpandedBundleItemProps; id: string | number };

export type ItemsSummaryViewProps = {
  summaryItems: SummaryItem[];
};

export const ItemsSummaryView = ({ summaryItems }: ItemsSummaryViewProps) => {
  const { __ } = useTranslations();
  const titleId = useId();
  const contentId = useId();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const toggleCollapse = () => setIsCollapsed(!isCollapsed);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 id={titleId} className={styles.title}>
          {__("cart.items_summary.title")}
        </h3>
        <button
          className={styles.toggleButton}
          onClick={toggleCollapse}
          aria-labelledby={titleId}
          aria-expanded={!isCollapsed}
          aria-controls={contentId}
        >
          <Icon aria-hidden="true" icon={isCollapsed ? ChevronDownIcon : ChevronUpIcon} />
        </button>
      </div>

      <div id={contentId} className={styles.content} hidden={isCollapsed}>
        {summaryItems.map((item, index) => (
          <Fragment key={item.id}>
            {item.type === "product" ? <ProductLineItem {...item.item} /> : <ExpandedBundleItem {...item.item} />}
            {index < summaryItems.length - 1 && (
              <div className={styles.separator}>
                <Separator />
              </div>
            )}
          </Fragment>
        ))}
      </div>
    </div>
  );
};
